import { useEffect, useRef } from "react";
import { formatNumber, useT } from "@/i18n";
import { desktopScreenRecordingMessages } from "@/i18n/messages/desktop-screen-recording";
import { cn } from "@/lib/cn";
import { isTauri } from "@/lib/is-tauri";
import {
  loadScreenRecordingMedia,
  useScreenRecordingMedia,
} from "./screen-recording-media";

/** Local playback is available independently of upload completion. */
export function ScreenRecordingVideo({
  clipId,
  preview = false,
  poster,
}: {
  clipId: string;
  preview?: boolean;
  poster?: string;
}) {
  const t = useT(desktopScreenRecordingMessages);
  const media = useScreenRecordingMedia((state) => state.clips[clipId]);
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (isTauri()) void loadScreenRecordingMedia(clipId).catch(() => {});
  }, [clipId]);
  useEffect(() => {
    const video = videoRef.current;
    return () => video?.pause();
  }, [media?.url]);
  return (
    <>
      {media?.url ? (
        <video
          ref={videoRef}
          src={media.url}
          poster={poster}
          controls
          playsInline
          autoPlay={preview}
          controlsList="nofullscreen nodownload"
          disablePictureInPicture
          preload="metadata"
          className={cn(
            "w-full rounded-preview",
            preview ? "max-h-56" : "max-h-32",
          )}
          aria-label={t("playSaved")}
          data-testid={`screen-recording-${preview ? "preview-video" : "video"}-${clipId}`}
        />
      ) : (
        <>
          {poster ? (
            <img
              src={poster}
              alt=""
              className="max-h-56 w-full rounded-preview object-contain"
              data-testid={`screen-recording-preview-poster-${clipId}`}
            />
          ) : null}
          {media?.error ? (
            <p
              role="alert"
              data-testid={`screen-recording-preview-error-${clipId}`}
            >
              {t("previewFailed", { error: media.error })}
            </p>
          ) : (
            <span role="status">{t("loadingRecording")}</span>
          )}
        </>
      )}
    </>
  );
}

export function ScreenRecordingUploadStatus({ clipId }: { clipId: string }) {
  const t = useT(desktopScreenRecordingMessages);
  const media = useScreenRecordingMedia((state) => state.clips[clipId]);
  if (!media?.uploading && !media?.error) return null;
  return (
    <div className="flex max-w-64 flex-col gap-1 text-appkit-caption">
      {media?.uploading ? (
        <span role="status" data-testid={`screen-recording-upload-${clipId}`}>
          {media.progress === undefined
            ? t("preparingUpload")
            : t("uploading", { percent: formatNumber(media.progress) })}
        </span>
      ) : null}
      {media?.error ? (
        <span
          role="status"
          data-testid={`screen-recording-upload-error-${clipId}`}
        >
          {media.url
            ? media.error
            : t("previewUnavailable", { error: media.error })}
        </span>
      ) : null}
    </div>
  );
}
